import fs from "fs";
import path from "path";
import type { ProjectSnapshot } from "../models/Snapshot.js";
import { SNAPSHOT_DIR, SNAPSHOT_INDEX } from "./snapshotTypes.js";

const SNAPSHOT_DIR_LEGACY = ".rn-orchestrator/snapshots";
const SNAPSHOT_INDEX_LEGACY = ".rn-orchestrator/snapshots/index.json";

/**
 * Persists a snapshot to disk.
 * Stores each snapshot as its own JSON file and updates an index.
 */
export function saveSnapshot(projectRoot: string, snapshot: ProjectSnapshot): void {
  const dir = path.join(projectRoot, SNAPSHOT_DIR);
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true });
  }

  // Write snapshot file
  const snapshotPath = path.join(dir, `${snapshot.id}.json`);
  fs.writeFileSync(snapshotPath, JSON.stringify(snapshot, null, 2), "utf-8");

  // Update index
  const indexPath = path.join(projectRoot, SNAPSHOT_INDEX);
  const index = loadSnapshotIndex(projectRoot);
  index.push({ id: snapshot.id, label: snapshot.label, createdAt: snapshot.createdAt });
  fs.writeFileSync(indexPath, JSON.stringify(index, null, 2), "utf-8");
}

/**
 * Loads the snapshot index (list of metadata without full snapshot data).
 */
export function loadSnapshotIndex(
  projectRoot: string
): { id: string; label: string; createdAt: string }[] {
  const newPath = path.join(projectRoot, SNAPSHOT_INDEX);
  const legacyPath = path.join(projectRoot, SNAPSHOT_INDEX_LEGACY);

  let indexPath = newPath;
  let hasLegacy = false;

  if (fs.existsSync(newPath)) {
    indexPath = newPath;
  } else if (fs.existsSync(legacyPath)) {
    indexPath = legacyPath;
    hasLegacy = true;
  } else {
    return [];
  }

  if (hasLegacy) {
    console.warn("Legacy rn-orchestrator configuration detected. Please migrate to .kyro/config.json");
  }

  try {
    const raw = fs.readFileSync(indexPath, "utf-8");
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Loads a specific snapshot by ID.
 */
export function loadSnapshot(projectRoot: string, id: string): ProjectSnapshot | null {
  const newPath = path.join(projectRoot, SNAPSHOT_DIR, `${id}.json`);
  const legacyPath = path.join(projectRoot, SNAPSHOT_DIR_LEGACY, `${id}.json`);

  let snapshotPath = newPath;
  let hasLegacy = false;

  if (fs.existsSync(newPath)) {
    snapshotPath = newPath;
  } else if (fs.existsSync(legacyPath)) {
    snapshotPath = legacyPath;
    hasLegacy = true;
  } else {
    return null;
  }

  if (hasLegacy) {
    console.warn("Legacy rn-orchestrator configuration detected. Please migrate to .kyro/config.json");
  }

  try {
    const raw = fs.readFileSync(snapshotPath, "utf-8");
    return JSON.parse(raw) as ProjectSnapshot;
  } catch {
    return null;
  }
}

/**
 * Returns all snapshots in full.
 */
export function loadAllSnapshots(projectRoot: string): ProjectSnapshot[] {
  const index = loadSnapshotIndex(projectRoot);
  const snapshots: ProjectSnapshot[] = [];
  for (const entry of index) {
    const snap = loadSnapshot(projectRoot, entry.id);
    if (snap) snapshots.push(snap);
  }
  return snapshots;
}
